import { TFunction } from 'i18next'
import {
  TimelineItem, ToolActivity, ToolActivityWire, TRACE_MARKER,
} from '../state/types'

export type UserItem = Extract<TimelineItem, { kind: 'user' }>

/**
 * One stretch of the thread: either something the operator said, or a contiguous run of agent work.
 * Task 3 collapses the `work` segments; a `turn` is never collapsed.
 */
export type Segment =
  | { kind: 'turn'; item: UserItem }
  | { kind: 'work'; items: TimelineItem[] }

/** Wall-clock HH:MM for a timeline item's stamp. `0` means the item carries no stamp. */
export function clockTime(at: number): string {
  if (!at) return ''
  const d = new Date(at)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Split a timeline into operator turns and the runs of work between them, in order. */
export function threadSegments(items: TimelineItem[]): Segment[] {
  const out: Segment[] = []
  items.forEach((item) => {
    if (item.kind === 'user') {
      out.push({ kind: 'turn', item })
      return
    }
    const last = out[out.length - 1]
    if (last && last.kind === 'work') last.items.push(item)
    else out.push({ kind: 'work', items: [item] })
  })
  return out
}

export interface ActivitySummary {
  documents: number
  apiCalls: number
  durationMs: number
}

export type ToolItem = Extract<TimelineItem, { kind: 'tool' }>

/**
 * The step a frame describes, or `undefined` when it carries none we trust — an older service's frame,
 * or a malformed one. `undefined` sends the item down the old-shape fallbacks, which is what they
 * exist for; a half-valid value would be worse, because nothing downstream would know to distrust it.
 */
export function toActivity(data: ToolActivityWire | null | undefined): ToolActivity | undefined {
  if (!data) return undefined
  if (data.activity === 'api') {
    if (typeof data.step !== 'string' || !data.step) return undefined
    if (data.methodClass !== 'read' && data.methodClass !== 'write') return undefined
    const id = data.declarationId
    const step: ToolActivity = { type: 'api', step: data.step, methodClass: data.methodClass }
    return Number.isSafeInteger(id) && (id as number) > 0 ? { ...step, declarationId: id } : step
  }
  if (data.activity === 'read') {
    return typeof data.document === 'string' && data.document
      ? { type: 'read', document: data.document }
      : { type: 'read' }
  }
  return undefined
}

/**
 * What a tool row says. A step is translated by its key (`copilot.step.<key>`), falling back to the
 * service's English `summary` for a key this build does not know. An old-shape item shows `summary`
 * as it always did — on a rolled-back service that is still `"<METHOD> <path>"`, already in its stream.
 */
export function stepLabel(t: TFunction, item: ToolItem): string {
  const a = item.activity
  if (!a) return item.summary
  if (a.type === 'api') return String(t(`copilot.step.${a.step}`, { defaultValue: item.summary }))
  // Its own namespace, not `copilot.step.*`: a future service step named `read` must not pick up a
  // `{{document}}` template.
  return a.document
    ? String(t('copilot.readStep.document', { document: a.document, defaultValue: item.summary }))
    : String(t('copilot.readStep.reference', { defaultValue: item.summary }))
}

/** An OLD service's `tool_use` summaries: `"<METHOD> <path>"` (an API call) or `"Read <basename>"`. */
const API_CALL = /^(GET|POST|PUT|PATCH|DELETE) \//

export function isApiCall(summary: string): boolean {
  return API_CALL.test(summary)
}

/** The admin-only `tool` item that records a turn's stream trace id and session id. */
export function isTraceMarker(summary: string): boolean {
  return summary.startsWith(TRACE_MARKER)
}

/**
 * One trace marker summary per segment, index-aligned with the input. Every turn posts its own
 * message, so a work segment carries its own marker or none; the last one wins if a segment ever
 * holds several. Turn segments carry nothing.
 */
export function resolveSegmentTraces(segments: Segment[]): (string | undefined)[] {
  return segments.map((seg) => {
    if (seg.kind === 'turn') return undefined
    let own: string | undefined
    seg.items.forEach((i) => {
      if (i.kind === 'tool' && isTraceMarker(i.summary)) own = i.summary
    })
    return own
  })
}

/**
 * The basenames the operator actually attached this session — for OLD-shape frames only. An older
 * service reports every file the agent opened as a bare `Read <basename>`, so this set was the only way
 * to tell an input document from a reference file. The current service names the document itself.
 */
export function attachedFileNames(items: TimelineItem[]): Set<string> {
  const names = new Set<string>()
  items.forEach((i) => { if (i.kind === 'user') i.files.forEach((f) => names.add(f)) })
  return names
}

/**
 * Counts for one run of work. DOCUMENTS, not reads: one set of names, so a document read twice, read
 * in parts, or read as its PDF and its text layer counts once (T1-3894).
 *
 * A current frame names its `document`, and that is counted as given — NOT checked against
 * `attached`, because an email's body and its attachments are documents the operator never attached by
 * name. Only an OLD-shape item (no `activity`) falls back to matching its `Read <name>` against
 * `attached`, which comes from the whole thread: a work segment holds no `user` items of its own.
 */
export function summarizeActivity(items: TimelineItem[], attached: Set<string>): ActivitySummary {
  const documents = new Set<string>()
  let apiCalls = 0
  items.forEach((i) => {
    if (i.kind !== 'tool') return
    if (i.activity) {
      if (i.activity.type === 'api') apiCalls += 1
      else if (i.activity.document) documents.add(i.activity.document)
      return
    }
    if (isApiCall(i.summary)) { apiCalls += 1; return }
    const name = i.summary.startsWith('Read ') ? i.summary.slice(5) : ''
    if (name && attached.has(name)) documents.add(name)
  })
  const stamps = items.map((i) => i.at).filter((a) => a > 0)
  const durationMs = stamps.length > 1 ? Math.max(...stamps) - Math.min(...stamps) : 0
  return { documents: documents.size, apiCalls, durationMs }
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const min = Math.floor(total / 60)
  const sec = total % 60
  return min > 0 ? `${min} min ${sec} s` : `${sec} s`
}

/**
 * Split the thread into the collapsible activity log and the final answer.
 *
 * The service emits every assistant text block as a `status` frame, interleaved with tools, so the
 * final answer is exactly the trailing contiguous run of `status` items after the last tool call.
 * While the run is still going there is no final answer yet, so everything stays in the log (which
 * renders expanded, so the operator watches the work live).
 */
export function splitTimeline(
  items: TimelineItem[], terminal: boolean,
): { activity: TimelineItem[]; answer: TimelineItem[] } {
  if (!terminal) return { activity: items, answer: [] }
  let cut = items.length
  while (cut > 0 && items[cut - 1].kind === 'status') cut -= 1
  return { activity: items.slice(0, cut), answer: items.slice(cut) }
}
